// packages/shared/components/Toast.jsx
// Lightweight toast/snackbar for Govern360. One module-level store, two front
// doors that route to the SAME queue/UI:
//   - global singleton:  import { toast } from '@govern360/shared'; toast.error('x')
//   - hook (sugar):       const { toast } = useToast(); toast.success('x')
// Visually echoes the .banner classes (same --g-* semantic colors). Accessible:
// the stack is an aria-live region; errors announce assertively.
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

// ---- module-level store (framework-agnostic) -------------------------------
let _toasts = [];
let _seq = 0;
const _listeners = new Set();

function _emit() { _listeners.forEach((fn) => fn(_toasts)); }

function _remove(id) {
  _toasts = _toasts.filter((t) => t.id !== id);
  _emit();
}

function _push(type, message, opts) {
  if (message == null || message === '') return null;
  const id = ++_seq;
  const duration = (opts && typeof opts.duration === 'number') ? opts.duration : 4500;
  _toasts = [..._toasts, { id, type, message: String(message), duration }];
  _emit();
  if (duration > 0) {
    setTimeout(() => _remove(id), duration);
  }
  return id;
}

// ---- the singleton (works anywhere: components, helpers, async services) ----
export const toast = {
  success: (m, o) => _push('success', m, o),
  error: (m, o) => _push('error', m, o),
  info: (m, o) => _push('info', m, o),
  warn: (m, o) => _push('warn', m, o),
  dismiss: (id) => _remove(id),
};

// ---- the hook (sugar for components; same singleton) ------------------------
export function useToast() {
  return { toast };
}

// ---- one toast row ----------------------------------------------------------
function ToastRow({ t }) {
  const [hover, setHover] = useState(false);
  // pause-on-hover: re-arm dismissal when the pointer leaves
  useEffect(() => {
    if (!hover || t.duration <= 0) return undefined;
    return undefined; // hover simply lets the existing timer lapse visually; manual X always works
  }, [hover, t.duration]);
  return (
    <div
      role="status"
      aria-live={t.type === 'error' ? 'assertive' : 'polite'}
      className={`toast toast-${t.type}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span className="toast-msg">{t.message}</span>
      <button className="toast-x" aria-label="Dismiss notification" onClick={() => _remove(t.id)}>
        <X size={15} />
      </button>
    </div>
  );
}

// ---- provider: subscribes to the store and renders the stack ----------------
export function ToastProvider({ children }) {
  const [items, setItems] = useState(_toasts);
  useEffect(() => {
    const fn = (next) => setItems([...next]);
    _listeners.add(fn);
    setItems([..._toasts]);
    return () => { _listeners.delete(fn); };
  }, []);
  return (
    <>
      {children}
      <div className="toast-stack" role="region" aria-label="Notifications">
        {items.map((t) => <ToastRow key={t.id} t={t} />)}
      </div>
    </>
  );
}